import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

const default2x4Id = "default2x4";
const default2x6Id = "default2x6";
const default2x8Id = "default2x8";
const default2x10Id = "default2x10";
const default2x12Id = "default2x12";

interface DeepEqualOptions {
  readonly omit?: { [key: string]: boolean };
  readonly ignoreUndefined?: boolean;
  readonly ignoreNullish?: boolean;
}

export function deepequalWithOptions(a: any, b: any, options?: DeepEqualOptions) {
  if (a === b) {
    return true;
  }

  if (a && b && typeof a === "object" && typeof b === "object") {
    if (a.constructor !== b.constructor) {
      return false;
    }

    if (a.constructor === RegExp) {
      return a.source === b.source && a.flags === b.flags;
    }

    const { omit = {}, ignoreUndefined = false, ignoreNullish = false } = options ?? {};

    const aCopy = { ...a };
    const bCopy = { ...b };

    for (const key in omit) {
      delete aCopy[key];
      delete bCopy[key];
    }

    a = aCopy;
    b = bCopy;

    let length, i;
    if (Array.isArray(a) || (ArrayBuffer.isView(a) && !(a instanceof DataView))) {
      // TypedArrays all have defined length, but ts thinks a and b might have some other type.
      length = (a as any[]).length;
      if (length !== (b as any[]).length) {
        return false;
      }
      for (i = length; i-- !== 0; ) {
        if (!deepequalWithOptions((a as any[])[i], (b as any[])[i], options)) {
          return false;
        }
      }
      return true;
    }

    if (a.valueOf !== Object.prototype.valueOf) {
      return a.valueOf() === b.valueOf();
    }
    if (a.toString !== Object.prototype.toString) {
      return a.toString() === b.toString();
    }

    let aKeys = ignoreUndefined ? Object.keys(a).filter((key) => a[key] !== undefined) : Object.keys(a);
    let bKeys = ignoreUndefined ? Object.keys(b).filter((key) => b[key] !== undefined) : Object.keys(b);

    if (ignoreNullish) {
      aKeys = aKeys.filter(
        (key) =>
          a[key] !== undefined &&
          a[key] !== null &&
          !(typeof a[key] === "string" && a[key] === "") &&
          !(typeof a[key] === "boolean" && a[key] === false),
      );
      bKeys = bKeys.filter(
        (key) =>
          b[key] !== undefined &&
          b[key] !== null &&
          !(typeof b[key] === "string" && b[key] === "") &&
          !(typeof b[key] === "boolean" && b[key] === false),
      );
    }

    length = aKeys.length;
    if (length !== bKeys.length) {
      return false;
    }

    for (i = length; i-- !== 0; ) {
      if (!Object.prototype.hasOwnProperty.call(b, aKeys[i])) {
        return false;
      }
    }

    for (i = length; i-- !== 0; ) {
      const key = aKeys[i];
      if (!deepequalWithOptions(a[key], b[key], options)) {
        return false;
      }
    }

    return true;
  }

  // true if both NaN, false otherwise
  return a !== a && b !== b;
}

const builtInFramingComponents = {
  [default2x4Id]: {
    entityType: "FRAMING_COMPONENT",
    id: default2x4Id,
    sortIndex: 0,
    type: "LUMBER",
    lumberProfileType: "SPF_2X4",
    name: "2x4 Lumber",
  },
  [default2x6Id]: {
    entityType: "FRAMING_COMPONENT",
    id: default2x6Id,
    sortIndex: 0,
    type: "LUMBER",
    lumberProfileType: "SPF_2X6",
    name: "2x6 Lumber",
  },
  [default2x8Id]: {
    entityType: "FRAMING_COMPONENT",
    id: default2x8Id,
    sortIndex: 0,
    type: "LUMBER",
    lumberProfileType: "SPF_2X8",
    name: "2x8 Lumber",
  },
  [default2x10Id]: {
    entityType: "FRAMING_COMPONENT",
    id: default2x10Id,
    sortIndex: 0,
    type: "LUMBER",
    lumberProfileType: "SPF_2X10",
    name: "2x10 Lumber",
  },
  [default2x12Id]: {
    entityType: "FRAMING_COMPONENT",
    id: default2x12Id,
    sortIndex: 0,
    type: "LUMBER",
    lumberProfileType: "SPF_2X12",
    name: "2x12 Lumber",
  },
};

const defaultFrameFloorAssemblyId = "DEFAULT-FRAME-FLOOR-ASSEMBLY";
const defaultSlabFloorAssemblyId = "DEFAULT-SLAB-FLOOR-ASSEMBLY";
const defaultSlabComponentId = "defaultSlab";
const defaultSheathingComponentId = "defaultSheathing";

// Default Frame Floor Assembly Components
const builtInFrameFloorAssemblies = {
  [defaultFrameFloorAssemblyId]: {
    entityType: "FRAME_FLOOR_ASSEMBLY",
    id: defaultFrameFloorAssemblyId,
    name: "Standard Wood Frame Assembly",
    joistComponentId: default2x12Id,
    subFloorComponentId: defaultSheathingComponentId,
    joistSpacing: 16,
    spanDirection: "SHORTEST_SPAN",
    sortIndex: 0,
  },
};

const builtInSlabFloorAssemblies = {
  [defaultSlabFloorAssemblyId]: {
    entityType: "SLAB_FLOOR_ASSEMBLY",
    id: defaultSlabFloorAssemblyId,
    name: "Standard Concrete Slab Assembly",
    slabComponentId: defaultSlabComponentId,
    subFloorComponentId: defaultSheathingComponentId,
    sortIndex: 0,
  },
};

// Default Slab Components
const builtInSlabFloorComponents = {
  [defaultSlabComponentId]: {
    entityType: "SLAB_COMPONENT",
    id: defaultSlabComponentId,
    name: "Standard Concrete Slab",
    slabType: "CIP",
    thickness: 6,
    sortIndex: 0,
  },
};

// Default SubFloor Components
const builtInSheathingComponents = {
  [defaultSheathingComponentId]: {
    entityType: "SHEATHING_COMPONENT",
    id: defaultSheathingComponentId,
    name: '3/4" Ply Subfloor',
    sheathingType: "PLYWOOD",
    thickness: 3 / 4,
    profile: "3/4-u",
    spanRating: "SR16",
    sortIndex: 0,
  },
};

const oldDefaultIdsToNewIdsMapping = {
  "HOUSE-FLR-AUTO": defaultFrameFloorAssemblyId,
  defaultFramingFloor: default2x12Id,
  defaultSlab: defaultSlabComponentId,
  defaultSubFloor: defaultSheathingComponentId,
} as const;

const wallMaterialIdToLumberProfile = {
  defaultStructuralMaterial2X4: "SPF_2X4",
  defaultStructuralMaterial2X6: "SPF_2X6",
  defaultStructuralMaterial2X8: "SPF_2X8",
  defaultStructuralMaterial2X10: "SPF_2X10",
  defaultStructuralMaterial2X12: "SPF_2X12",
};

const DocEntityPrefix = {
  FrameFloorAssembly: "frameFloorAssembly",
  SlabFloorAssembly: "slabFloorAssembly",
  FramingComponent: "framingComponent",
  SlabComponent: "slabComponent",
  SheathingComponent: "sheathingComponent",
} as const;

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_508: DocMigration = {
  maxSchemaVersion: "0.0.508",
  description: "Migrate floor assemblies and components to catalogs",
  apply(json: any): any {
    // Initialize groups and entityGroups if they don't exist
    if (!json.groups) {
      json.groups = {};
    }
    if (!json.entityGroups) {
      json.entityGroups = {};
    }
    // Initialize entityRefs if it doesn't exist
    if (!json.entityRefs) {
      json.entityRefs = {};
    }
    json.framingComponents = {
      ...json.framingComponents,
      ...builtInFramingComponents,
    };
    json.slabComponents = {
      ...json.slabComponents,
      ...builtInSlabFloorComponents,
    };
    json.sheathingComponents = {
      ...json.sheathingComponents,
      ...builtInSheathingComponents,
    };
    json.frameFloorAssemblies = {
      ...json.frameFloorAssemblies,
      ...builtInFrameFloorAssemblies,
    };
    json.slabFloorAssemblies = {
      ...json.slabFloorAssemblies,
      ...builtInSlabFloorAssemblies,
    };

    // Delete old default floor assembly IDs
    for (const oldDefaultFloorId of Object.keys(oldDefaultIdsToNewIdsMapping)) {
      if (oldDefaultFloorId !== "defaultSlab" && oldDefaultFloorId !== "defaultSubFloor") {
        delete json.frameFloorAssemblies[oldDefaultFloorId];
        delete json.slabFloorAssemblies[oldDefaultFloorId];
      }
    }

    const generateUniqueId = (oldId: string, settingsId: string, table: Record<string, any>): string => {
      let suffix = 0;
      let newId = `${oldId}_${settingsId}_${suffix}`;
      while (table[newId]) {
        suffix += 1;
        newId = `${oldId}_${settingsId}_${suffix}`;
      }
      return newId;
    };

    // Track which entities belong to which settings set
    const settingsSetToFramingComponents = new Map<string, string[]>();
    const settingsSetToSlabComponents = new Map<string, string[]>();
    const settingsSetToSheathingComponents = new Map<string, string[]>();
    const settingsSetToFrameFloorAssemblies = new Map<string, string[]>();
    const settingsSetToSlabFloorAssemblies = new Map<string, string[]>();

    // First go through all floor components and deduplicate them
    // This map is duplicate id to a list of [settingsSetId, newComponentId]
    const remappedFramingComponents = new Map<string, [string, string][]>();
    const remappedSlabComponents = new Map<string, [string, string][]>();
    const remappedSheathingComponents = new Map<string, [string, string][]>();

    for (const settingsId in json.settings) {
      const setting = json.settings[settingsId];
      if (setting.type !== "FLOOR_CATALOG_SETTINGS") {
        continue;
      }
      const settingsSetId = setting.parentId;
      // Extract docRef information from the settings set
      const docRefId = json.entityRefs[settingsSetId]?.docRefId;
      const docRefDisabled = json.entityRefs[settingsSetId]?.disabled;

      for (const floorComponentId in setting.floorComponents) {
        // Skip built-in components
        if (
          builtInFramingComponents[floorComponentId] ||
          builtInSlabFloorComponents[floorComponentId] ||
          builtInSheathingComponents[floorComponentId] ||
          Object.keys(oldDefaultIdsToNewIdsMapping).includes(floorComponentId)
        ) {
          continue;
        }
        const floorComponent = setting.floorComponents[floorComponentId];
        let componentEntity: any;
        let targetMap: Map<string, [string, string][]>;
        let targetCollection: any;

        if (floorComponent.componentType === "STRUCTURE") {
          if (floorComponent.structuralType === "SLAB") {
            componentEntity = {
              entityType: "SLAB_COMPONENT",
              slabType: "CIP",
              thickness: floorComponent.thickness,
              id: floorComponentId,
              name: floorComponent.name,
              sortIndex: 0,
            };
            targetMap = remappedSlabComponents;
            targetCollection = json.slabComponents;
          } else {
            if (
              floorComponent.joistType === "CUSTOM_JOISTS" ||
              wallMaterialIdToLumberProfile[floorComponent.joistProfileId] === undefined
            ) {
              componentEntity = {
                entityType: "FRAMING_COMPONENT",
                type: "CUSTOM",
                id: floorComponentId,
                name: floorComponent.name,
                sortIndex: 0,
                width: floorComponent.thickness,
                height: 1.5, // This is the height for all current lumber profiles
              };
            } else {
              componentEntity = {
                entityType: "FRAMING_COMPONENT",
                type: "LUMBER",
                lumberProfileType: wallMaterialIdToLumberProfile[floorComponent.joistProfileId],
                id: floorComponentId,
                name: floorComponent.name,
                sortIndex: 0,
              };
            }
            targetMap = remappedFramingComponents;
            targetCollection = json.framingComponents;
          }
        } else {
          componentEntity = {
            entityType: "SHEATHING_COMPONENT",
            sheathingType: floorComponent.substrateProfileType, // PLYWOOD or OSB
            profile: floorComponent.substrateProfileId,
            spanRating: floorComponent.spanRating,
            thickness: floorComponent.thickness,
            id: floorComponentId,
            name: floorComponent.name,
            sortIndex: 0,
          };
          targetMap = remappedSheathingComponents;
          targetCollection = json.sheathingComponents;
        }

        let finalComponentId = floorComponentId;
        if (targetCollection[floorComponentId]) {
          if (
            !deepequalWithOptions(targetCollection[floorComponentId], componentEntity, {
              omit: { name: true },
              ignoreUndefined: true,
            })
          ) {
            let collectionName: string;
            if (targetCollection === json.framingComponents) {
              collectionName = "framingComponents";
            } else if (targetCollection === json.slabComponents) {
              collectionName = "slabComponents";
            } else {
              collectionName = "sheathingComponents";
            }
            console.warn(
              `Floor component with id ${floorComponentId} already exists in ${collectionName}. Generating new id.`,
            );
            // generate a new stable id for the component having an id collision
            const newComponentId = generateUniqueId(floorComponentId, settingsId, targetCollection);
            targetMap.set(floorComponentId, [
              ...(targetMap.get(floorComponentId) ?? []),
              [setting.parentId, newComponentId],
            ]);
            targetCollection[newComponentId] = {
              ...componentEntity,
              id: newComponentId,
            };
            finalComponentId = newComponentId;
          }
        } else {
          targetCollection[floorComponentId] = componentEntity;
        }

        // If the settings were doc-ref'd, add the migrated component to entity refs
        if (docRefId) {
          let entityType: string;
          if (targetMap === remappedFramingComponents) {
            entityType = "FRAMING_COMPONENT";
          } else if (targetMap === remappedSlabComponents) {
            entityType = "SLAB_COMPONENT";
          } else {
            entityType = "SHEATHING_COMPONENT";
          }
          json.entityRefs[finalComponentId] = {
            id: finalComponentId,
            docRefId,
            entityType,
            disabled: docRefDisabled,
            sortIndex: 0,
          };
        }

        // Track component for this settings set
        if (targetMap === remappedFramingComponents) {
          if (!settingsSetToFramingComponents.has(settingsSetId)) {
            settingsSetToFramingComponents.set(settingsSetId, []);
          }
          settingsSetToFramingComponents.get(settingsSetId)?.push(finalComponentId);
        } else if (targetMap === remappedSlabComponents) {
          if (!settingsSetToSlabComponents.has(settingsSetId)) {
            settingsSetToSlabComponents.set(settingsSetId, []);
          }
          settingsSetToSlabComponents.get(settingsSetId)?.push(finalComponentId);
        } else if (targetMap === remappedSheathingComponents) {
          if (!settingsSetToSheathingComponents.has(settingsSetId)) {
            settingsSetToSheathingComponents.set(settingsSetId, []);
          }
          settingsSetToSheathingComponents.get(settingsSetId)?.push(finalComponentId);
        }
      }
    }

    // Now go through all floor assemblies and deduplicate them
    const remappedFrameFloorAssemblies = new Map<string, [string, string][]>();
    const remappedSlabFloorAssemblies = new Map<string, [string, string][]>();
    const referenceKeys = ["structureId", "subFloorId"];
    // Track settings sets that had floor catalog settings deleted
    const settingsSetIdsWithDeletedSettings = new Set<string>();

    for (const settingsId in json.settings) {
      const setting = json.settings[settingsId];
      if (setting.type !== "FLOOR_CATALOG_SETTINGS") {
        continue;
      }
      const settingsSetId = setting.parentId;
      // Extract docRef information from the settings set
      const docRefId = json.entityRefs[settingsSetId]?.docRefId;
      const docRefDisabled = json.entityRefs[settingsSetId]?.disabled;

      for (const floorAssemblyId in setting.floorAssemblies) {
        if (Object.keys(oldDefaultIdsToNewIdsMapping).includes(floorAssemblyId)) {
          continue;
        }
        const floorAssembly = setting.floorAssemblies[floorAssemblyId];
        const oldStructureComponent = setting.floorComponents[floorAssembly.structureId];
        if (!oldStructureComponent) {
          continue;
        }
        // Update component references to use remapped IDs
        for (const key of referenceKeys) {
          if (floorAssembly[key] === undefined) {
            continue;
          }
          if (Object.keys(oldDefaultIdsToNewIdsMapping).includes(floorAssembly[key])) {
            floorAssembly[key] = oldDefaultIdsToNewIdsMapping[floorAssembly[key]];
            continue;
          }
          let remaps: [string, string][] = [];
          if (key === "structureId" && oldStructureComponent.structuralType === "FRAMING") {
            remaps = remappedFramingComponents.get(floorAssembly[key]) ?? [];
          } else if (key === "structureId" && oldStructureComponent.structuralType === "SLAB") {
            remaps = remappedSlabComponents.get(floorAssembly[key]) ?? [];
          } else if (key === "subFloorId") {
            remaps = remappedSheathingComponents.get(floorAssembly[key]) ?? [];
          }
          for (const [settingsSetId, newComponentId] of remaps) {
            if (settingsSetId === setting.parentId) {
              floorAssembly[key] = newComponentId;
              break;
            }
          }
        }
        const structureComponent =
          oldStructureComponent.structuralType === "SLAB"
            ? json.slabComponents[floorAssembly.structureId]
            : json.framingComponents[floorAssembly.structureId];
        const subFloorComponent = json.sheathingComponents[floorAssembly.subFloorId];
        if (!structureComponent || !subFloorComponent) {
          continue;
        }

        let assemblyEntity: any;
        let targetMap: Map<string, [string, string][]>;
        let targetCollection: any;

        if (oldStructureComponent.structuralType === "SLAB") {
          assemblyEntity = {
            entityType: "SLAB_FLOOR_ASSEMBLY",
            id: floorAssemblyId,
            name: floorAssembly.name,
            slabComponentId: floorAssembly.structureId,
            subFloorComponentId: floorAssembly.subFloorId,
            sortIndex: 0,
          };
          targetMap = remappedSlabFloorAssemblies;
          targetCollection = json.slabFloorAssemblies;
        } else {
          assemblyEntity = {
            entityType: "FRAME_FLOOR_ASSEMBLY",
            id: floorAssemblyId,
            name: floorAssembly.name,
            joistComponentId: floorAssembly.structureId,
            subFloorComponentId: floorAssembly.subFloorId,
            joistSpacing: oldStructureComponent?.joistSpacing ?? 16,
            spanDirection: oldStructureComponent?.spanDirection ?? "SHORTEST_SPAN",
            sortIndex: 0,
          };
          targetMap = remappedFrameFloorAssemblies;
          targetCollection = json.frameFloorAssemblies;
        }

        let finalAssemblyId = floorAssemblyId;
        if (targetCollection[floorAssemblyId]) {
          if (
            !deepequalWithOptions(targetCollection[floorAssemblyId], assemblyEntity, {
              omit: { name: true },
              ignoreUndefined: true,
            })
          ) {
            console.warn(
              `Floor assembly with id ${floorAssemblyId} already exists in ${
                targetCollection === json.frameFloorAssemblies ? "frameFloorAssemblies" : "slabFloorAssemblies"
              }. Generating new id.`,
            );
            // generate a new stable id for the assembly having an id collision
            const newAssemblyId = generateUniqueId(floorAssemblyId, settingsId, targetCollection);
            targetMap.set(floorAssemblyId, [
              ...(targetMap.get(floorAssemblyId) ?? []),
              [setting.parentId, newAssemblyId],
            ]);
            targetCollection[newAssemblyId] = {
              ...assemblyEntity,
              id: newAssemblyId,
            };
            finalAssemblyId = newAssemblyId;
          }
        } else {
          targetCollection[floorAssemblyId] = assemblyEntity;
        }

        // If the settings were doc-ref'd, add the migrated assembly to entity refs
        if (docRefId) {
          const entityType =
            targetMap === remappedFrameFloorAssemblies ? "FRAME_FLOOR_ASSEMBLY" : "SLAB_FLOOR_ASSEMBLY";
          json.entityRefs[finalAssemblyId] = {
            id: finalAssemblyId,
            docRefId,
            entityType,
            disabled: docRefDisabled,
            sortIndex: 0,
          };
        }

        // Track assembly for this settings set
        if (targetMap === remappedFrameFloorAssemblies) {
          if (!settingsSetToFrameFloorAssemblies.has(settingsSetId)) {
            settingsSetToFrameFloorAssemblies.set(settingsSetId, []);
          }
          settingsSetToFrameFloorAssemblies.get(settingsSetId)?.push(finalAssemblyId);
        } else if (targetMap === remappedSlabFloorAssemblies) {
          if (!settingsSetToSlabFloorAssemblies.has(settingsSetId)) {
            settingsSetToSlabFloorAssemblies.set(settingsSetId, []);
          }
          settingsSetToSlabFloorAssemblies.get(settingsSetId)?.push(finalAssemblyId);
        }
      }
      // Track this settings set before deleting the floor catalog settings
      settingsSetIdsWithDeletedSettings.add(settingsSetId);
      // Clean up entityRefs for the deleted settings
      delete json.settings[settingsId];
      delete json.entityRefs[settingsId];
    }

    // Check if settings sets are empty after migration and delete them if so
    for (const settingsSetId of settingsSetIdsWithDeletedSettings) {
      // Check if there are any remaining settings in this settings set
      const hasRemainingSettings = Object.values(json.settings).some(
        (setting: any) => setting.parentId === settingsSetId,
      );

      if (!hasRemainingSettings && json.settingsSets?.[settingsSetId]) {
        delete json.settingsSets[settingsSetId];
        delete json.entityRefs[settingsSetId];
      }
    }

    // Go through all doc entities that might be referencing the old default or duplicate floor assemblies and update them
    for (const settingsId in json.settings) {
      const settings = json.settings[settingsId];
      if (settings.type !== "FLOOR_SETTINGS") {
        continue;
      }
      if (!settings.floorAssembly) {
        continue;
      }
      const floorAssemblyId = settings.floorAssembly;
      if (Object.keys(oldDefaultIdsToNewIdsMapping).includes(floorAssemblyId)) {
        json.settings[settingsId].floorAssembly = oldDefaultIdsToNewIdsMapping[floorAssemblyId];
      }
      if (remappedFrameFloorAssemblies.has(floorAssemblyId)) {
        const remaps = remappedFrameFloorAssemblies.get(floorAssemblyId) ?? [];
        for (const [settingsSetId, newAssemblyId] of remaps) {
          if (settingsSetId === settings.parentId) {
            json.settings[settingsId].floorAssembly = newAssemblyId;
            break;
          }
        }
      }
      if (remappedSlabFloorAssemblies.has(floorAssemblyId)) {
        const remaps = remappedSlabFloorAssemblies.get(floorAssemblyId) ?? [];
        for (const [settingsSetId, newAssemblyId] of remaps) {
          if (settingsSetId === settings.parentId) {
            json.settings[settingsId].floorAssembly = newAssemblyId;
            break;
          }
        }
      }
    }

    // Update levels that reference floor assemblies
    for (const levelId in json.levels) {
      const level = json.levels[levelId];
      if (!level.floorAssemblyId) {
        continue;
      }
      if (Object.keys(oldDefaultIdsToNewIdsMapping).includes(level.floorAssemblyId)) {
        json.levels[levelId].floorAssemblyId = oldDefaultIdsToNewIdsMapping[level.floorAssemblyId];
      }
      if (remappedFrameFloorAssemblies.has(level.floorAssemblyId)) {
        const remaps = remappedFrameFloorAssemblies.get(level.floorAssemblyId) ?? [];
        // For levels, we don't know which settings set the level works with at any point in time.
        // TODO: prod query.  Use first one for now
        for (const remap of remaps) {
          const newAssemblyId = remap[1];
          json.levels[levelId].floorAssemblyId = newAssemblyId;
          break;
        }
      }
      if (remappedSlabFloorAssemblies.has(level.floorAssemblyId)) {
        const remaps = remappedSlabFloorAssemblies.get(level.floorAssemblyId) ?? [];
        for (const remap of remaps) {
          const newAssemblyId = remap[1];
          json.levels[levelId].floorAssemblyId = newAssemblyId;
          break;
        }
      }
    }

    // Update floors that reference floor assemblies
    for (const floorId in json.floors) {
      const floor = json.floors[floorId];
      if (!floor.assemblyId && !floor.structure && !floor.subFloor) {
        continue;
      }
      if (Object.keys(oldDefaultIdsToNewIdsMapping).includes(floor.assemblyId)) {
        json.floors[floorId].assemblyId = oldDefaultIdsToNewIdsMapping[floor.assemblyId];
      }
      if (Object.keys(oldDefaultIdsToNewIdsMapping).includes(floor.structure)) {
        json.floors[floorId].structure = oldDefaultIdsToNewIdsMapping[floor.structure];
      }
      if (Object.keys(oldDefaultIdsToNewIdsMapping).includes(floor.subFloor)) {
        json.floors[floorId].subFloor = oldDefaultIdsToNewIdsMapping[floor.subFloor];
      }
      if (remappedFrameFloorAssemblies.has(floor.assemblyId)) {
        const remaps = remappedFrameFloorAssemblies.get(floor.assemblyId) ?? [];
        for (const remap of remaps) {
          const newAssemblyId = remap[1];
          json.floors[floorId].assemblyId = newAssemblyId;
          break;
        }
      }
      if (remappedSlabFloorAssemblies.has(floor.assemblyId)) {
        const remaps = remappedSlabFloorAssemblies.get(floor.assemblyId) ?? [];
        for (const remap of remaps) {
          const newAssemblyId = remap[1];
          json.floors[floorId].assemblyId = newAssemblyId;
          break;
        }
      }
      if (remappedFramingComponents.has(floor.structure)) {
        const remaps = remappedFramingComponents.get(floor.structure) ?? [];
        for (const remap of remaps) {
          const newStructureId = remap[1];
          json.floors[floorId].structure = newStructureId;
          break;
        }
      }
      if (remappedSlabComponents.has(floor.structure)) {
        const remaps = remappedSlabComponents.get(floor.structure) ?? [];
        for (const remap of remaps) {
          const newStructureId = remap[1];
          json.floors[floorId].structure = newStructureId;
          break;
        }
      }
      if (remappedSheathingComponents.has(floor.subFloor)) {
        const remaps = remappedSheathingComponents.get(floor.subFloor) ?? [];
        for (const remap of remaps) {
          const newSubFloorId = remap[1];
          json.floors[floorId].subFloor = newSubFloorId;
          break;
        }
      }
    }

    // Create groups for floor assemblies and components
    // Get all unique settings set IDs that have floor entities
    const allSettingsSetIds = new Set([
      ...settingsSetToFramingComponents.keys(),
      ...settingsSetToSlabComponents.keys(),
      ...settingsSetToSheathingComponents.keys(),
      ...settingsSetToFrameFloorAssemblies.keys(),
      ...settingsSetToSlabFloorAssemblies.keys(),
    ]);

    // Calculate the maximum sortIndex for groups
    let maxGroupSortIndex = 0;
    for (const groupId in json.groups) {
      const group = json.groups[groupId];
      if (group.sortIndex !== undefined && group.sortIndex > maxGroupSortIndex) {
        maxGroupSortIndex = group.sortIndex;
      }
    }

    for (const settingsSetId of allSettingsSetIds) {
      const settingsSet = json.settingsSets?.[settingsSetId];
      const settingsSetName = settingsSet?.name ?? "Untitled Settings Set";

      const framingComponents = settingsSetToFramingComponents.get(settingsSetId) ?? [];
      const slabComponents = settingsSetToSlabComponents.get(settingsSetId) ?? [];
      const sheathingComponents = settingsSetToSheathingComponents.get(settingsSetId) ?? [];
      const frameFloorAssemblies = settingsSetToFrameFloorAssemblies.get(settingsSetId) ?? [];
      const slabFloorAssemblies = settingsSetToSlabFloorAssemblies.get(settingsSetId) ?? [];

      // Create group for frame floor assemblies if there are any
      if (frameFloorAssemblies.length > 0) {
        const assemblyGroupId = uid();
        maxGroupSortIndex += 1;
        const assemblyGroupSortIndex = maxGroupSortIndex;
        json.groups[assemblyGroupId] = {
          entityType: "GROUP",
          id: assemblyGroupId,
          name: `${settingsSetName}`,
          sortIndex: assemblyGroupSortIndex,
          parentId: undefined,
          disabled: false,
        };

        // Sort assemblies alphanumerically by name
        const sortedFrameFloorAssemblies = [...frameFloorAssemblies].sort((a, b) => {
          const nameA = json.frameFloorAssemblies[a]?.name ?? "";
          const nameB = json.frameFloorAssemblies[b]?.name ?? "";
          return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
        });

        // Add entries to entityGroups and set sortIndex for each assembly
        for (let i = 0; i < sortedFrameFloorAssemblies.length; i++) {
          const assemblyId = sortedFrameFloorAssemblies[i];
          const assemblyHash = `${DocEntityPrefix.FrameFloorAssembly}.${assemblyId}`;
          json.entityGroups[assemblyHash] = assemblyGroupId;
          // Set sortIndex relative to the group's sortIndex (children come after the group)
          if (json.frameFloorAssemblies[assemblyId]) {
            json.frameFloorAssemblies[assemblyId] = {
              ...json.frameFloorAssemblies[assemblyId],
              sortIndex: assemblyGroupSortIndex + i + 1,
            };
          }
        }
      }

      // Create group for slab floor assemblies if there are any
      if (slabFloorAssemblies.length > 0) {
        const assemblyGroupId = uid();
        maxGroupSortIndex += 1;
        const assemblyGroupSortIndex = maxGroupSortIndex;
        json.groups[assemblyGroupId] = {
          entityType: "GROUP",
          id: assemblyGroupId,
          name: `${settingsSetName}`,
          sortIndex: assemblyGroupSortIndex,
          parentId: undefined,
          disabled: false,
        };

        // Sort assemblies alphanumerically by name
        const sortedSlabFloorAssemblies = [...slabFloorAssemblies].sort((a, b) => {
          const nameA = json.slabFloorAssemblies[a]?.name ?? "";
          const nameB = json.slabFloorAssemblies[b]?.name ?? "";
          return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
        });

        // Add entries to entityGroups and set sortIndex for each assembly
        for (let i = 0; i < sortedSlabFloorAssemblies.length; i++) {
          const assemblyId = sortedSlabFloorAssemblies[i];
          const assemblyHash = `${DocEntityPrefix.SlabFloorAssembly}.${assemblyId}`;
          json.entityGroups[assemblyHash] = assemblyGroupId;
          // Set sortIndex relative to the group's sortIndex (children come after the group)
          if (json.slabFloorAssemblies[assemblyId]) {
            json.slabFloorAssemblies[assemblyId] = {
              ...json.slabFloorAssemblies[assemblyId],
              sortIndex: assemblyGroupSortIndex + i + 1,
            };
          }
        }
      }

      // Create group for framing components if there are any
      if (framingComponents.length > 0) {
        const componentGroupId = uid();
        maxGroupSortIndex += 1;
        const componentGroupSortIndex = maxGroupSortIndex;
        json.groups[componentGroupId] = {
          entityType: "GROUP",
          id: componentGroupId,
          name: `${settingsSetName}`,
          sortIndex: componentGroupSortIndex,
          parentId: undefined,
          disabled: false,
        };

        // Sort components alphanumerically by name
        const sortedFramingComponents = [...framingComponents].sort((a, b) => {
          const nameA = json.framingComponents[a]?.name ?? "";
          const nameB = json.framingComponents[b]?.name ?? "";
          return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
        });

        // Add entries to entityGroups and set sortIndex for each component
        for (let i = 0; i < sortedFramingComponents.length; i++) {
          const componentId = sortedFramingComponents[i];
          const componentHash = `${DocEntityPrefix.FramingComponent}.${componentId}`;
          json.entityGroups[componentHash] = componentGroupId;
          // Set sortIndex relative to the group's sortIndex (children come after the group)
          if (json.framingComponents[componentId]) {
            json.framingComponents[componentId] = {
              ...json.framingComponents[componentId],
              sortIndex: componentGroupSortIndex + i + 1,
            };
          }
        }
      }

      // Create group for slab components if there are any
      if (slabComponents.length > 0) {
        const componentGroupId = uid();
        maxGroupSortIndex += 1;
        const componentGroupSortIndex = maxGroupSortIndex;
        json.groups[componentGroupId] = {
          entityType: "GROUP",
          id: componentGroupId,
          name: `${settingsSetName}`,
          sortIndex: componentGroupSortIndex,
          parentId: undefined,
          disabled: false,
        };

        // Sort components alphanumerically by name
        const sortedSlabComponents = [...slabComponents].sort((a, b) => {
          const nameA = json.slabComponents[a]?.name ?? "";
          const nameB = json.slabComponents[b]?.name ?? "";
          return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
        });

        // Add entries to entityGroups and set sortIndex for each component
        for (let i = 0; i < sortedSlabComponents.length; i++) {
          const componentId = sortedSlabComponents[i];
          const componentHash = `${DocEntityPrefix.SlabComponent}.${componentId}`;
          json.entityGroups[componentHash] = componentGroupId;
          // Set sortIndex relative to the group's sortIndex (children come after the group)
          if (json.slabComponents[componentId]) {
            json.slabComponents[componentId] = {
              ...json.slabComponents[componentId],
              sortIndex: componentGroupSortIndex + i + 1,
            };
          }
        }
      }

      // Create group for sheathing components if there are any
      if (sheathingComponents.length > 0) {
        const componentGroupId = uid();
        maxGroupSortIndex += 1;
        const componentGroupSortIndex = maxGroupSortIndex;
        json.groups[componentGroupId] = {
          entityType: "GROUP",
          id: componentGroupId,
          name: `${settingsSetName}`,
          sortIndex: componentGroupSortIndex,
          parentId: undefined,
          disabled: false,
        };

        // Sort components alphanumerically by name
        const sortedSheathingComponents = [...sheathingComponents].sort((a, b) => {
          const nameA = json.sheathingComponents[a]?.name ?? "";
          const nameB = json.sheathingComponents[b]?.name ?? "";
          return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
        });

        // Add entries to entityGroups and set sortIndex for each component
        for (let i = 0; i < sortedSheathingComponents.length; i++) {
          const componentId = sortedSheathingComponents[i];
          const componentHash = `${DocEntityPrefix.SheathingComponent}.${componentId}`;
          json.entityGroups[componentHash] = componentGroupId;
          // Set sortIndex relative to the group's sortIndex (children come after the group)
          if (json.sheathingComponents[componentId]) {
            json.sheathingComponents[componentId] = {
              ...json.sheathingComponents[componentId],
              sortIndex: componentGroupSortIndex + i + 1,
            };
          }
        }
      }
    }

    // Create groups for built-in entities
    const builtInFrameFloorAssemblyGroupId = uid();
    maxGroupSortIndex += 1;
    const builtInFrameFloorAssemblyGroupSortIndex = maxGroupSortIndex;
    json.groups[builtInFrameFloorAssemblyGroupId] = {
      entityType: "GROUP",
      id: builtInFrameFloorAssemblyGroupId,
      name: "Built-in Frame Floor Assemblies",
      sortIndex: builtInFrameFloorAssemblyGroupSortIndex,
      parentId: undefined,
      disabled: false,
      isBuiltIn: true,
    };

    // Sort built-in assemblies alphanumerically by name
    const sortedBuiltInFrameFloorAssemblyIds = Object.keys(builtInFrameFloorAssemblies).sort((a, b) => {
      const nameA = builtInFrameFloorAssemblies[a]?.name ?? "";
      const nameB = builtInFrameFloorAssemblies[b]?.name ?? "";
      return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
    });

    for (let i = 0; i < sortedBuiltInFrameFloorAssemblyIds.length; i++) {
      const assemblyId = sortedBuiltInFrameFloorAssemblyIds[i];
      const assemblyHash = `${DocEntityPrefix.FrameFloorAssembly}.${assemblyId}`;
      json.entityGroups[assemblyHash] = builtInFrameFloorAssemblyGroupId;
      // Set sortIndex relative to the group's sortIndex (children come after the group)
      if (json.frameFloorAssemblies[assemblyId]) {
        json.frameFloorAssemblies[assemblyId] = {
          ...json.frameFloorAssemblies[assemblyId],
          sortIndex: builtInFrameFloorAssemblyGroupSortIndex + i + 1,
        };
      }
    }

    const builtInSlabFloorAssemblyGroupId = uid();
    maxGroupSortIndex += 1;
    const builtInSlabFloorAssemblyGroupSortIndex = maxGroupSortIndex;
    json.groups[builtInSlabFloorAssemblyGroupId] = {
      entityType: "GROUP",
      id: builtInSlabFloorAssemblyGroupId,
      name: "Built-in Slab Floor Assemblies",
      sortIndex: builtInSlabFloorAssemblyGroupSortIndex,
      parentId: undefined,
      disabled: false,
      isBuiltIn: true,
    };

    // Sort built-in assemblies alphanumerically by name
    const sortedBuiltInSlabFloorAssemblyIds = Object.keys(builtInSlabFloorAssemblies).sort((a, b) => {
      const nameA = builtInSlabFloorAssemblies[a]?.name ?? "";
      const nameB = builtInSlabFloorAssemblies[b]?.name ?? "";
      return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
    });

    for (let i = 0; i < sortedBuiltInSlabFloorAssemblyIds.length; i++) {
      const assemblyId = sortedBuiltInSlabFloorAssemblyIds[i];
      const assemblyHash = `${DocEntityPrefix.SlabFloorAssembly}.${assemblyId}`;
      json.entityGroups[assemblyHash] = builtInSlabFloorAssemblyGroupId;
      // Set sortIndex relative to the group's sortIndex (children come after the group)
      if (json.slabFloorAssemblies[assemblyId]) {
        json.slabFloorAssemblies[assemblyId] = {
          ...json.slabFloorAssemblies[assemblyId],
          sortIndex: builtInSlabFloorAssemblyGroupSortIndex + i + 1,
        };
      }
    }

    const builtInFramingComponentGroupId = uid();
    maxGroupSortIndex += 1;
    const builtInFramingComponentGroupSortIndex = maxGroupSortIndex;
    json.groups[builtInFramingComponentGroupId] = {
      entityType: "GROUP",
      id: builtInFramingComponentGroupId,
      name: "Built-in Framing Components",
      sortIndex: builtInFramingComponentGroupSortIndex,
      parentId: undefined,
      disabled: false,
      isBuiltIn: true,
    };

    // Sort built-in components alphanumerically by name
    const sortedBuiltInFramingComponentIds = Object.keys(builtInFramingComponents).sort((a, b) => {
      const nameA = builtInFramingComponents[a]?.name ?? "";
      const nameB = builtInFramingComponents[b]?.name ?? "";
      return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
    });

    for (let i = 0; i < sortedBuiltInFramingComponentIds.length; i++) {
      const componentId = sortedBuiltInFramingComponentIds[i];
      const componentHash = `${DocEntityPrefix.FramingComponent}.${componentId}`;
      json.entityGroups[componentHash] = builtInFramingComponentGroupId;
      // Set sortIndex relative to the group's sortIndex (children come after the group)
      if (json.framingComponents[componentId]) {
        json.framingComponents[componentId] = {
          ...json.framingComponents[componentId],
          sortIndex: builtInFramingComponentGroupSortIndex + i + 1,
        };
      }
    }

    const builtInSlabComponentGroupId = uid();
    maxGroupSortIndex += 1;
    const builtInSlabComponentGroupSortIndex = maxGroupSortIndex;
    json.groups[builtInSlabComponentGroupId] = {
      entityType: "GROUP",
      id: builtInSlabComponentGroupId,
      name: "Built-in Slab Components",
      sortIndex: builtInSlabComponentGroupSortIndex,
      parentId: undefined,
      disabled: false,
      isBuiltIn: true,
    };

    // Sort built-in components alphanumerically by name
    const sortedBuiltInSlabComponentIds = Object.keys(builtInSlabFloorComponents).sort((a, b) => {
      const nameA = builtInSlabFloorComponents[a]?.name ?? "";
      const nameB = builtInSlabFloorComponents[b]?.name ?? "";
      return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
    });

    for (let i = 0; i < sortedBuiltInSlabComponentIds.length; i++) {
      const componentId = sortedBuiltInSlabComponentIds[i];
      const componentHash = `${DocEntityPrefix.SlabComponent}.${componentId}`;
      json.entityGroups[componentHash] = builtInSlabComponentGroupId;
      // Set sortIndex relative to the group's sortIndex (children come after the group)
      if (json.slabComponents[componentId]) {
        json.slabComponents[componentId] = {
          ...json.slabComponents[componentId],
          sortIndex: builtInSlabComponentGroupSortIndex + i + 1,
        };
      }
    }

    const builtInSheathingComponentGroupId = uid();
    maxGroupSortIndex += 1;
    const builtInSheathingComponentGroupSortIndex = maxGroupSortIndex;
    json.groups[builtInSheathingComponentGroupId] = {
      entityType: "GROUP",
      id: builtInSheathingComponentGroupId,
      name: "Built-in Sheathing Components",
      sortIndex: builtInSheathingComponentGroupSortIndex,
      parentId: undefined,
      disabled: false,
      isBuiltIn: true,
    };

    // Sort built-in components alphanumerically by name
    const sortedBuiltInSheathingComponentIds = Object.keys(builtInSheathingComponents).sort((a, b) => {
      const nameA = builtInSheathingComponents[a]?.name ?? "";
      const nameB = builtInSheathingComponents[b]?.name ?? "";
      return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
    });

    for (let i = 0; i < sortedBuiltInSheathingComponentIds.length; i++) {
      const componentId = sortedBuiltInSheathingComponentIds[i];
      const componentHash = `${DocEntityPrefix.SheathingComponent}.${componentId}`;
      json.entityGroups[componentHash] = builtInSheathingComponentGroupId;
      // Set sortIndex relative to the group's sortIndex (children come after the group)
      if (json.sheathingComponents[componentId]) {
        json.sheathingComponents[componentId] = {
          ...json.sheathingComponents[componentId],
          sortIndex: builtInSheathingComponentGroupSortIndex + i + 1,
        };
      }
    }

    return json;
  },
};

export default migration_0_0_508;
